"use client";

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname, useRouter } from "next/navigation";

type StartParams = {
  title: string;
  gradientClass: string; // tailwind background class e.g. 'bg-zinc-900'
  titleClass: string;    // tailwind text color class e.g. 'text-zinc-900'
  originRect: { left: number; top: number; width: number; height: number };
  path: string;
  exitTarget?: { x: number; y: number }; // absolute viewport coords to exit toward
};

type OverlayContextValue = {
  start: (params: StartParams) => void;
};

const OverlayContext = createContext<OverlayContextValue | null>(null);

export const useOverlayTransition = (): OverlayContextValue => {
  const ctx = useContext(OverlayContext);
  if (!ctx) throw new Error("useOverlayTransition must be used within OverlayProvider");
  return ctx;
};

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname();

  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState("");
  const [gradientClass, setGradientClass] = useState("from-gray-900 via-gray-800 to-gray-900");
  const [titleClass, setTitleClass] = useState("text-zinc-900");
  const [origin, setOrigin] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [phase, setPhase] = useState<"enter" | "exitOpposite">("enter");
  const exitScheduledRef = useRef(false);
  const [exitTarget, setExitTarget] = useState<{ x: number; y: number } | null>(null);
  const lastPathRef = useRef(pathname);

  // Timing
  const EXPAND_MS = 1000;
  const HOLD_MS = 250;
  const EXIT_MS = 1000;
  const EASE = [0.4, 0, 0.2, 1] as const; // smoother ease-in-out

  const start = useCallback(({ title, gradientClass, titleClass, originRect, path, exitTarget }: StartParams) => {
    setOrigin(originRect);
    setExitTarget(exitTarget ?? null);
    setTitle(title);
    setGradientClass(gradientClass);
    setTitleClass(titleClass);
    setPhase("enter");
    setVisible(true);

    // Navigate immediately; overlay persists across route change
    lastPathRef.current = pathname;
    router.push(path);

    // Fallback: if route doesn't change promptly, still run exit
    exitScheduledRef.current = false;
    const fallback = window.setTimeout(() => {
      if (exitScheduledRef.current) return;
      exitScheduledRef.current = true;
      setPhase("exitOpposite");
      window.setTimeout(() => {
        setVisible(false);
        exitScheduledRef.current = false;
      }, EXIT_MS + 40);
    }, EXPAND_MS + HOLD_MS + 120);

    // Listen for page-ready signal and exit immediately when received
    const onReady = () => {
      if (exitScheduledRef.current) return;
      exitScheduledRef.current = true;
      window.clearTimeout(fallback);
      setPhase("exitOpposite");
      window.setTimeout(() => {
        setVisible(false);
        exitScheduledRef.current = false;
      }, EXIT_MS + 40);
      window.removeEventListener('overlay:ready', onReady);
    };
    window.addEventListener('overlay:ready', onReady);
  }, [pathname, router]);

  // When the path changes (new page mounted), trigger exit toward opposite corner
  React.useEffect(() => {
    if (!visible) return;
    if (lastPathRef.current === pathname) return;
    if (exitScheduledRef.current) return;
    exitScheduledRef.current = true;

    // Enter + brief hold before exiting on the new page
    const t = window.setTimeout(() => {
      setPhase("exitOpposite");
      // After exit completes, hide overlay smoothly
      const off = window.setTimeout(() => {
        setVisible(false);
        exitScheduledRef.current = false;
      }, EXIT_MS + 40);
      return () => window.clearTimeout(off);
    }, EXPAND_MS + HOLD_MS);
    return () => window.clearTimeout(t);
  }, [pathname, visible]);

  // Compute motion targets
  const computeTargets = () => {
    if (!origin) {
      return {
        initial: { x: 0, y: 0, width: 0, height: 0, borderRadius: "12px" },
        full: { x: 0, y: 0, width: "100vw", height: "100vh", borderRadius: "0px" },
        exit: { x: 0, y: 0, width: 80, height: 80, borderRadius: "16px" },
      };
    }

    const initial = {
      x: origin.left,
      y: origin.top,
      width: origin.width,
      height: origin.height,
      borderRadius: "12px",
    };

    const full = { x: 0, y: 0, width: "100vw", height: "100vh", borderRadius: "0px" } as const;

    // Exit target: use provided target if available; otherwise compute opposite corner
    const vw = typeof window !== "undefined" ? window.innerWidth : 1920;
    const vh = typeof window !== "undefined" ? window.innerHeight : 1080;
    const defaultExit = (() => {
      const centerX = origin.left + origin.width / 2;
      const centerY = origin.top + origin.height / 2;
      const targetLeft = centerX < vw / 2 ? vw - 80 : 0;
      const targetTop = centerY < vh / 2 ? vh - 80 : 0;
      return { x: targetLeft, y: targetTop };
    })();
    const chosen = exitTarget ?? defaultExit;
    const exit = { x: chosen.x, y: chosen.y, width: 80, height: 80, borderRadius: "16px" };

    return { initial, full, exit };
  };

  const targets = computeTargets();

  const value = useMemo(() => ({ start }), [start]);

  return (
    <OverlayContext.Provider value={value}>
      {children}
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            initial={targets.initial}
            animate={phase === "exitOpposite" ? targets.exit : targets.full}
            transition={{ duration: phase === "exitOpposite" ? EXIT_MS/1000 : EXPAND_MS/1000, ease: EASE }}
            className={`fixed z-[9999999] ${gradientClass} flex items-center justify-center overflow-hidden pointer-events-none will-change-transform`}
            style={{ left: 0, top: 0 }}
          >
            <motion.h1
              className={`text-6xl 2xl:text-8xl font-bold text-center ${titleClass}`}
              initial={{ opacity: 0.001, scale: 0.96, y: 8, letterSpacing: "0.06em" }}
              animate={phase === "exitOpposite"
                ? { opacity: 0, scale: 0.96, y: 8, letterSpacing: "0.06em" }
                : { opacity: 1, scale: 1, y: 0, letterSpacing: "0.03em" }
              }
              transition={{ duration: 0.26, ease: EASE }}
            >
              {title}
            </motion.h1>
          </motion.div>
        )}
      </AnimatePresence>
    </OverlayContext.Provider>
  );
};


